import {
  Column,
  Columns,
  SelectionListChangeEvent,
  SelectionListProps,
} from '@dixa/lib-shared-habla';
import { AsyncStatus } from 'lib-shared-types-api';
import debounce from 'lodash/debounce';
import {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUpdateEffect } from 'react-use';

import {
  CategorizedListCategory,
  CategorizedListCategoryNamedId,
} from './categorizedList.types';
import { CategorizedListCategories } from './components/categories/categorizedListCategories';
import { CategorizedListMenu } from './components/menu/categorizedListMenu';
import { CategorizedListSearch } from './components/search/categorizedListSearch';

/** Represents the categorized list props. */
export type CategorizedListProps<TItem = unknown> = Pick<
  SelectionListProps<TItem>,
  'isMultiple' | 'onChange' | 'isActive'
> & {
  /** The categories. */
  categories: readonly CategorizedListCategory<TItem>[];
  /** The selected items. */
  selectedItems: readonly TItem[];
  /** The async status of the categories. */
  asyncStatus: AsyncStatus;
  /** Whether there is a next page. */
  hasNextPage: boolean;
  /** Whether to show the search input. */
  showSearch?: boolean;
  /** Whether to reset the search when the list is inactive. */
  resetSearchOnInactive?: boolean;
  /** Whether to show the sidebar menu. */
  showMenu?: boolean;
  /** The width of the main column in pixels. */
  width?: number;
  /** The height of container in pixels. */
  height?: number;
  /** The content to slot in top of the categories. */
  categoriesTopSlot?: ReactNode;
  /** The item content component. */
  ItemContent: ComponentType;
  /** The create item content component. */
  CreateItemContent?: ComponentType;
  /**
   * Whether the item is favorite.
   *
   * @param item The item.
   */
  isItemFavorite: (item: TItem) => boolean;
  /**
   * Whether the item is disabled.
   *
   * @param item The item.
   */
  isItemDisabled?: (item: TItem) => boolean;
  /**
   * The identity function to obtain the identity of the item.
   *
   * @param item The item.
   */
  identityFn: (item: TItem) => string | number;
  /** Navigates to the next page. */
  nextPage: () => void;
  /** Re-fetches the categories. */
  refetch: () => void;
  /** Called when the search term changes. */
  onSearchChange?: (searchTerm: string) => void;
};

/**
 * Represent a component that presents a categorized list with an optional
 * search and bookmark menu.
 */
export const CategorizedList = <TItem,>({
  categories,
  selectedItems,
  asyncStatus,
  hasNextPage,
  showSearch = false,
  resetSearchOnInactive = false,
  showMenu = false,
  isMultiple = false,
  isActive = false,
  width = 300,
  height = 350,
  categoriesTopSlot,
  ItemContent,
  CreateItemContent,
  isItemFavorite,
  isItemDisabled,
  identityFn,
  nextPage,
  refetch,
  onSearchChange,
  onChange,
}: CategorizedListProps<TItem>): JSX.Element => {
  // Search state.
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  // Categories state.
  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const [categoriesElement, setCategoriesElement] =
    useState<HTMLUListElement | null>(null);
  const [categoriesSectionElements, setCategoriesSectionElements] = useState<
    Partial<Record<CategorizedListCategoryNamedId, HTMLLIElement>>
  >({});
  const [visibleCategoryIds, setVisibleCategoryIds] = useState<
    readonly CategorizedListCategoryNamedId[]
  >([]);

  const setTimeoutSearch = useCallback(
    (searchTerm: string) =>
      // We delay this one frame to ensure that the search term updates at the
      // same time as the arg search term changes in the paginated hook as
      // these needs to stay in sync.
      setTimeout(() => {
        setSearchTerm(searchTerm);
      }),
    [],
  );

  /** Handles changing the search. */
  const handleSearchChange = useMemo(
    () => debounce((searchTerm: string) => setTimeoutSearch(searchTerm), 300),
    [setTimeoutSearch],
  );

  /** Handles changing the menu item. */
  const handleMenuItemChange = useCallback(
    ({
      selectedItem: { value: category },
    }: SelectionListChangeEvent<CategorizedListCategory<TItem>>) => {
      const categoryElement = categoriesSectionElements[category.id];

      if (categoryElement && categoriesContainerRef.current) {
        // For the top category `li` we subtract the border width (top + bottom)
        // of the categories `ul` list so that the scroll position is at zero.
        const categoriesOffset = category.id === categories[0].id ? 2 : 0;
        const top =
          categoryElement.offsetTop -
          categoriesContainerRef.current.offsetTop -
          categoriesOffset;

        categoriesContainerRef.current.scrollTo({ top, behavior: 'smooth' });
      }
    },
    [categoriesSectionElements, categories],
  );

  /** Handles changing the category item. */
  const handleCategoryItemChange = useCallback(
    (event: SelectionListChangeEvent<TItem>) => {
      onChange?.(event);
      searchRef.current?.focus();
    },
    [onChange],
  );

  // Handles setting the focus on either the search input or the categories
  // selection list when the categorized list becomes active.
  useEffect(() => {
    if (isActive) {
      // This has to be done in the next event loop frame through a timeout as
      // the DOM elements would otherwise not be ready.
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        } else if (categoriesElement) {
          categoriesElement.focus();
        }
      });
    }
  }, [categoriesElement, isActive]);

  // Handles performing the search across the list.
  useUpdateEffect(() => {
    if (showSearch) {
      onSearchChange?.(searchTerm);
    }
  }, [searchTerm, showSearch, onSearchChange]);

  // Handles resetting the search term when and if required.
  useEffect(() => {
    if (showSearch && !isActive && resetSearchOnInactive) {
      setTimeoutSearch('');
    }
  }, [isActive, resetSearchOnInactive, showSearch, setTimeoutSearch]);

  return (
    <Columns style={{ height }}>
      {showMenu && (
        <Column size="150px">
          <Column.Row
            noHorizontalPadding
            noVerticalPadding
            noMinHeight
            style={{ padding: 'var(--dixa-size-spacer-200)' }}
          >
            <CategorizedListMenu
              categories={categories}
              visibleCategoryIds={visibleCategoryIds}
              onChange={handleMenuItemChange}
            />
          </Column.Row>
        </Column>
      )}

      <Column size={`${width}px`}>
        {showSearch && (
          <Column.Row
            noHorizontalPadding
            noVerticalPadding
            noMinHeight
            style={{
              padding: 'var(--dixa-size-spacer-200)',
              paddingBottom: 0,
            }}
          >
            <CategorizedListSearch
              ref={searchRef}
              value={searchTerm}
              setIsSearchFocused={setIsSearchFocused}
              onSearchChange={handleSearchChange}
            />
          </Column.Row>
        )}

        {categoriesTopSlot && (
          <Column.Row
            noHorizontalPadding
            noVerticalPadding
            noMinHeight
            style={{ padding: 'var(--dixa-size-spacer-200)' }}
          >
            {categoriesTopSlot}
          </Column.Row>
        )}

        <Column.Row
          noHorizontalPadding
          noVerticalPadding
          scrollable
          ref={categoriesContainerRef}
        >
          <CategorizedListCategories
            categories={categories}
            selectedItems={selectedItems}
            asyncStatus={asyncStatus}
            hasNextPage={hasNextPage}
            isMultiple={isMultiple}
            isActive={isSearchFocused}
            searchTerm={searchTerm}
            ItemContent={ItemContent}
            CreateItemContent={CreateItemContent}
            isItemFavorite={isItemFavorite}
            isItemDisabled={isItemDisabled}
            identityFn={identityFn}
            nextPage={nextPage}
            refetch={refetch}
            onChange={handleCategoryItemChange}
            setCategoriesElement={setCategoriesElement}
            setCategoriesSectionElements={setCategoriesSectionElements}
            setVisibleCategoryIds={setVisibleCategoryIds}
          />
        </Column.Row>
      </Column>
    </Columns>
  );
};
